import React from 'react';
import PropTypes from 'prop-types';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { cn } from 'src/lib/utils';
import { getInitials, paletteForSeed } from 'src/lib/avatar';

// "Panel & Prep" column — Interview tab only, off the same
// row.interview_details the When column reads (see InterviewWhenCell.jsx).
// Panel: first name bold, "+N" pill for the rest of panel_names — hovering
// the pill opens a Tooltip listing those remaining names (per explicit
// request + reference screenshot), reusing the same Tooltip primitive
// EmailStatusIcons.jsx already uses elsewhere in this tab set. Each name in
// that tooltip renders as a small avatar-initials + name row (same
// deterministic-color-circle language CandidateCell.jsx's own candidate
// avatars already use — src/lib/avatar.js) rather than a plain text pill —
// a bare chip read oddly next to every other person-shaped thing on this
// page, per explicit feedback. Prep: just the interview `format` (e.g. "In
// person") — the shared screenshot also showed a "brief & questions ready/
// used/pending" line, but nothing in interview_details backs that, so it's
// omitted per explicit instruction rather than invented.
//
// The tooltip's own "Also on the panel" header is a colored background
// band, not TooltipContent's built-in dot+uppercase `name` treatment — per
// explicit reference to ContactStatusIcons.jsx's own popover header
// (contactStatus.js's "neutral" tone wash), which carries the heading via a
// full-bleed tinted strip instead of a dot marker. Reused directly rather
// than through contactStatus.js's own exports, since those are scoped to
// email-status tones specifically; this is just the one neutral tone.
const InterviewPanelCell = ({ row }) => {
  const details = row.interview_details;
  const panelNames = details?.panel_names || [];

  if (panelNames.length === 0 && !details?.format) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  const [firstPanelist, ...rest] = panelNames;

  return (
    <div className="min-w-0">
      {firstPanelist && (
        <p className="text-sm font-semibold text-foreground-strong truncate">
          {firstPanelist}
          {rest.length > 0 && (
            <TooltipProvider>
              <Tooltip delayDuration={150}>
                <TooltipTrigger asChild>
                  <span className="ml-1.5 inline-flex items-center rounded-sm bg-muted px-[7px] py-[1px] text-[11px] font-semibold text-muted-foreground align-middle cursor-default">
                    +{rest.length}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="w-56 overflow-hidden p-0">
                  <div className="bg-slate-500/10 px-3.5 py-2 dark:bg-slate-400/15">
                    <span className="text-[11px] font-bold text-slate-600 dark:text-slate-300">
                      Also on the panel
                    </span>
                  </div>
                  <div className="flex flex-col gap-1.5 p-3">
                    {rest.map((name) => {
                      const { bg, text } = paletteForSeed(name);
                      return (
                        <div key={name} className="flex items-center gap-2">
                          <span
                            className={cn(
                              'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold',
                              bg,
                              text
                            )}
                          >
                            {getInitials(name)}
                          </span>
                          <span className="text-xs font-medium text-foreground-strong">
                            {name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </p>
      )}
      {details?.format && (
        <p className="text-xs text-muted-foreground truncate">
          {details.format}
        </p>
      )}
    </div>
  );
};

InterviewPanelCell.propTypes = {
  row: PropTypes.shape({
    interview_details: PropTypes.shape({
      panel_names: PropTypes.arrayOf(PropTypes.string),
      format: PropTypes.string,
    }),
  }).isRequired,
};

export const interviewPanelColumn = {
  key: 'interview_panel',
  header: 'Panel & Prep',
  className: 'text-sm',
  // Left-aligned per explicit request — every senior_level_flow column
  // reads left-to-right like the Candidate column, for a consistent
  // layout across tabs. The "+N" pill is inline-flex (inline-level), so
  // it flows and left-aligns along with the panelist name text on the
  // same line.
  align: 'left',
  // `width`, not `minWidth` — src/components/ui/data-table.jsx only ever
  // reads `column.width` (see its own style={{ width: column.width }});
  // `minWidth` here was silently ignored, leaving this column with no
  // explicit width at all and letting table-fixed's equal-share default
  // take over instead of the 180px every sibling senior-flow column uses.
  width: '180px',
  render: (_v, row) => <InterviewPanelCell row={row} />,
};

export default InterviewPanelCell;
